import { observer } from 'mobx-react-lite'
import cx from 'classnames'
import { Item } from '@react-stately/collections'
import { ComboBox, ComboBoxProps } from 'components/ComboBox/ComboBox'
import { useState } from 'react'
import Styles from './BrandKitFontPicker.module.scss'
import { FontFamilyComboBox } from 'components/VisualStudioPlayground/FontFamily/FontFamilyComboBox'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { Key } from 'react'
import { useRootStore } from 'stores/RootStore'

// Helper function to find closest weight
export const findClosestWeight = (weights: FontWeightType[], targetWeight: number): FontWeightType => {
  return weights.reduce((prev, curr) => {
    return Math.abs(curr - targetWeight) < Math.abs(prev - targetWeight) ? curr : prev
  })
}

interface BrandKitFontPickerProps {
  selectedFontFamily?: string
  selectedWeight?: FontWeightType
  selectedFontSize?: number
  onSelectionChange: (
    fontFamily: Key,
    weight: FontWeightType,
    fontSize: number,
    availableWeights: FontWeightType[],
  ) => void
  brandKitFontFamilies?: string[]
  placeholder?: string
  'aria-label'?: string
  size?: 'sm' | 'md'
  showFontPreview?: boolean
  defaultFontSize?: number
}

export const BrandKitFontPicker = observer(
  ({
    selectedFontFamily,
    selectedWeight,
    selectedFontSize,
    onSelectionChange,
    brandKitFontFamilies,
    placeholder = 'Select a font',
    'aria-label': ariaLabel = 'Font family',
    size = 'md',
    showFontPreview = true,
    defaultFontSize = 16,
  }: BrandKitFontPickerProps) => {
    const [isSearching, setIsSearching] = useState(false)
    const { fonts } = useRootStore()
    const fontLoadError = selectedFontFamily && !fonts.get(selectedFontFamily)

    // Create style object for font preview - include both family and size
    const fontPreviewStyle =
      showFontPreview && selectedFontFamily
        ? ({
            '--preview-font-family': selectedFontFamily,
            '--preview-font-size': `${selectedFontSize || defaultFontSize}px`,
          } as React.CSSProperties)
        : undefined

    const baseProps: Partial<ComboBoxProps> = {
      'aria-label': ariaLabel,
      size: size,
      wrapperClassName: cx(Styles.comboboxWrapper, { [Styles.error]: fontLoadError }),
      contentsClassName: Styles.comboboxContents,
      inputClassName: cx(Styles.comboboxInput, { [Styles.fontPreview]: showFontPreview && selectedFontFamily }),
      popoverClassName: Styles.comboboxPopover,
    }

    if (!isSearching) {
      return (
        <div className={Styles.fontWrapper} style={fontPreviewStyle}>
          <ComboBox {...baseProps} onFocus={() => setIsSearching(true)} inputValue={selectedFontFamily || placeholder}>
            <Item>{selectedFontFamily || placeholder}</Item>
          </ComboBox>
          {fontLoadError && (
            <span className={Styles.errorText}>Custom font is missing. Re-upload it or choose another font.</span>
          )}
        </div>
      )
    }

    return (
      <div style={fontPreviewStyle}>
        <FontFamilyComboBox
          {...baseProps}
          autoFocus
          allowUpload={false}
          placeholder="Search fonts..."
          menuTrigger="focus"
          onBlur={() => setIsSearching(false)}
          onSelectionChange={(newFamily, weights) => {
            if (!newFamily) return

            // Find the closest weight to the currently selected weight, or default to first available
            const targetWeight = selectedWeight || weights[0]
            const closestWeight = findClosestWeight(weights, targetWeight)

            // Use current font size or default
            const fontSize = selectedFontSize || defaultFontSize

            onSelectionChange(newFamily, closestWeight, fontSize, weights)
            setIsSearching(false)
          }}
          selectedKey={selectedFontFamily}
          defaultInputValue=""
          popoverMaxHeight={300}
          brandKitFontFamilies={brandKitFontFamilies}
        />
      </div>
    )
  },
)
